import React, { useState } from "react";

export type ImageVariant = "image" | "profile" | "blog" | "thumbnail";

export interface ImageProps {
  src?: string;
  alt: string;
  className?: string;
  /**
   * Controls the default fallback icon + how the error state renders.
   * - image/profile: fallback shows a bordered wrapper with an icon (+ text)
   * - blog/thumbnail: fallback renders a bare icon so the parent controls sizing
   */
  variant?: ImageVariant;
  fallbackIcon?: React.ReactNode;
  fallbackText?: string;
  loading?: "lazy" | "eager";
  decoding?: "async" | "sync" | "auto";
  width?: number;
  height?: number;
  onError?: () => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

const ICONS: Record<ImageVariant, JSX.Element> = {
  image: (
    <svg className="h-full w-full text-gray-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </svg>
  ),
  profile: (
    <svg className="h-full w-full text-gray-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
    </svg>
  ),
  blog: (
    <svg className="h-full w-full text-gray-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
    </svg>
  ),
  thumbnail: (
    <svg className="h-full w-full text-gray-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z" />
    </svg>
  ),
};

/**
 * Unified image-with-fallback + loading-shimmer primitive. Consolidates the
 * former ImageWithFallback / ProfileImageWithFallback / BlogImageWithFallback
 * trio behind a single `variant` prop.
 */
const Image: React.FC<ImageProps> = ({
  src,
  alt,
  className = "",
  variant = "image",
  fallbackIcon,
  fallbackText,
  loading,
  decoding,
  width,
  height,
  onError,
  onLoad,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
    onError?.();
  };
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    onLoad?.(e);
  };

  const bare = variant === "blog" || variant === "thumbnail";
  const icon = fallbackIcon ?? ICONS[variant];

  if (hasError || !src) {
    if (bare) {
      return (
        <div className={`flex items-center justify-center text-gray-400 ${className}`}>
          {icon}
        </div>
      );
    }
    return (
      <div
        className={`flex items-center justify-center border border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900 ${className}`}
      >
        <div className="p-4 text-center">
          {icon}
          {(fallbackText || variant === "profile") && (
            <p className="mt-2 text-xs text-gray-400">
              {fallbackText ?? "No profile image"}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div
          className={`flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 ${className}`}
        >
          <span className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={`${className} ${
          isLoading
            ? bare
              ? "opacity-0"
              : "hidden"
            : bare
            ? "opacity-100"
            : ""
        }`}
        loading={
          loading ?? (variant === "thumbnail" || variant === "blog" ? "lazy" : "eager")
        }
        decoding={decoding ?? "async"}
        onError={handleError}
        onLoad={handleLoad}
      />
    </>
  );
};

export default Image;
